/**
 * Permission system for Vela.
 */

export type Permission =
  | 'can_view_finances'        // invoices, expenses, reports, bank-feeds
  | 'can_manage_invoices'       // create/edit/send invoices
  | 'can_view_expenses'         // view expenses
  | 'can_manage_expenses'       // create/edit expenses
  | 'can_view_payroll'          // view payroll data
  | 'can_run_payroll'           // execute payroll runs
  | 'can_manage_employees'      // create/edit/remove employees + assign roles
  | 'can_manage_roles'          // create/edit/delete custom roles
  | 'can_approve_leave'         // approve/reject leave requests
  | 'can_manage_noticeboard'    // create/delete announcements
  | 'can_send_emails'           // send emails to clients
  | 'can_manage_clients'        // manage client records
  | 'can_use_comms'             // use messaging
  | 'can_view_noticeboard'      // see announcements
  | 'can_view_directory'        // see employee directory
  | 'can_view_portal'           // see self-service portal
  | 'can_submit_resignation'    // submit resignation
  | 'can_review_resignation'    // review resignation requests
  | 'can_delete_employees'      // remove employees from org
  | 'can_view_sent_emails'      // view sent email log
  | 'can_view_customers'        // view clients
  ;

export interface PermissionDef {
  key: Permission;
  label: string;
  category: string;
}

export const PERMISSIONS_LIST: PermissionDef[] = [
  { key: 'can_view_finances', label: 'View finances', category: 'Accounting' },
  { key: 'can_manage_invoices', label: 'Manage invoices', category: 'Accounting' },
  { key: 'can_view_expenses', label: 'View expenses', category: 'Accounting' },
  { key: 'can_manage_expenses', label: 'Manage expenses', category: 'Accounting' },
  { key: 'can_view_payroll', label: 'View payroll', category: 'Payroll' },
  { key: 'can_run_payroll', label: 'Run payroll', category: 'Payroll' },
  { key: 'can_manage_employees', label: 'Manage employees', category: 'People' },
  { key: 'can_delete_employees', label: 'Delete employees', category: 'People' },
  { key: 'can_manage_roles', label: 'Manage roles', category: 'Roles' },
  { key: 'can_approve_leave', label: 'Approve leave', category: 'People' },
  { key: 'can_manage_noticeboard', label: 'Manage noticeboard', category: 'Workspace' },
  { key: 'can_send_emails', label: 'Send emails', category: 'Communication' },
  { key: 'can_manage_clients', label: 'Manage clients', category: 'Clients' },
  { key: 'can_view_customers', label: 'View clients', category: 'Clients' },
  { key: 'can_use_comms', label: 'Use messaging', category: 'Communication' },
  { key: 'can_view_noticeboard', label: 'View noticeboard', category: 'Workspace' },
  { key: 'can_view_directory', label: 'View directory', category: 'People' },
  { key: 'can_view_portal', label: 'View portal', category: 'Self-Service' },
  { key: 'can_submit_resignation', label: 'Submit resignation', category: 'Self-Service' },
  { key: 'can_review_resignation', label: 'Review resignations', category: 'People' },
  { key: 'can_view_sent_emails', label: 'View sent emails', category: 'Communication' },
];

export const PERMISSION_CATEGORIES = [
  'Accounting', 'Payroll', 'People', 'Roles', 'Workspace', 'Communication', 'Clients', 'Self-Service',
];

// Page → required permissions map
export const PAGE_PERMISSIONS: Record<string, Permission[]> = {
  invoices: ['can_view_finances'],
  expenses: ['can_view_finances'],
  'bank-feeds': ['can_view_finances'],
  reports: ['can_view_finances'],
  payroll: ['can_view_payroll'],
  employees: ['can_manage_employees'],
  leave: ['can_approve_leave'],
  performance: ['can_manage_employees'],
  documents: ['can_view_finances'],
  noticeboard: ['can_view_noticeboard'],
  comms: ['can_use_comms'],
  directory: ['can_view_directory'],
  portal: ['can_view_portal'],
  clients: ['can_view_customers'],
};

// Default permissions for built-in org roles
export const BUILTIN_ROLE_PERMISSIONS: Record<string, Permission[]> = {
  owner: [], // bypass — has everything
  admin: [], // bypass — has everything
  it_team: [], // bypass — full system access for IT
  accountant: ['can_view_finances', 'can_manage_invoices', 'can_view_expenses', 'can_view_payroll', 'can_use_comms', 'can_view_noticeboard', 'can_view_directory', 'can_view_portal', 'can_view_sent_emails', 'can_view_customers'],
  hr_manager: ['can_manage_employees', 'can_approve_leave', 'can_use_comms', 'can_view_noticeboard', 'can_manage_noticeboard', 'can_view_directory', 'can_view_portal', 'can_submit_resignation', 'can_review_resignation'],
  employee: ['can_use_comms', 'can_view_noticeboard', 'can_view_directory', 'can_view_portal', 'can_submit_resignation'],
  guest: [], // newly joined users — no access until reassigned
};

// Default hierarchy levels for built-in org roles
export const BUILTIN_ROLE_LEVELS: Record<string, number> = {
  owner: 100,
  admin: 99,
  it_team: 98,
  hr_manager: 5,
  accountant: 5,
  employee: 1,
  guest: 0,
};

export function hasBypassRole(role: string): boolean {
  return role === 'owner' || role === 'admin' || role === 'it_team';
}

export function hasBuiltInPermission(builtInRole: string, permission: Permission): boolean {
  if (hasBypassRole(builtInRole)) return true;
  return (BUILTIN_ROLE_PERMISSIONS[builtInRole] || []).includes(permission);
}

export function getHierarchyLevel(role: string): number {
  return BUILTIN_ROLE_LEVELS[role] || 0;
}

// Default custom roles to seed on org creation.
// These are the core department roles for small businesses.
// Each has: name, level, color, permissions, can_manage_lower.
export interface DefaultRoleDef {
  name: string;
  hierarchyLevel: number;
  color: string;
  permissions: Permission[];
  canManageLower: boolean;
}

export const DEFAULT_CUSTOM_ROLES: DefaultRoleDef[] = [
  {
    name: "Finance",
    hierarchyLevel: 5,
    color: "#10B981",
    permissions: ['can_view_finances', 'can_manage_invoices', 'can_view_expenses', 'can_manage_expenses', 'can_use_comms', 'can_view_noticeboard', 'can_view_directory', 'can_view_portal', 'can_view_sent_emails', 'can_view_customers'],
    canManageLower: false,
  },
  {
    name: "HR",
    hierarchyLevel: 5,
    color: "#8B5CF6",
    permissions: ['can_manage_employees', 'can_approve_leave', 'can_use_comms', 'can_view_noticeboard', 'can_manage_noticeboard', 'can_view_directory', 'can_view_portal', 'can_submit_resignation', 'can_review_resignation'],
    canManageLower: false,
  },
  {
    name: "Operations",
    hierarchyLevel: 5,
    color: "#F59E0B",
    permissions: ['can_use_comms', 'can_view_noticeboard', 'can_view_directory', 'can_view_portal'],
    canManageLower: false,
  },
];
